import { ChallengeService } from '@duck/api-definitions'
import {
  CommandLineIcon,
  InformationCircleIcon,
  PlayIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'
import { TranslationKey, useTar } from '@/modules'
import { DevPrint } from '@components/DevPrint'
import { StandardLoader } from '../StandardLoader'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from '@tanstack/react-router'
import { apis } from '../../modules/apiClients'
import { useState } from 'react'
import { challengeDifficultyDisplayMap, getChallengeDifficultyColor } from '@/utils/challengeData.utils'
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@duck/ui'
import { toast } from 'sonner'
import { availableChallengesQueryKeys } from '@/hooks/useAvailableChallenges'
import { queryClient } from '@/modules/queryClient'

interface Challenge extends ChallengeService.AvailableChallenge {
  icon?: React.ComponentType<{ className?: string }>
  estimatedTime?: string
}

interface ChallengeCardProps {
  challenge: Challenge
  onInfoClick?: (challenge: Challenge) => void
}

const actionButtonMap: Record<ChallengeService.AvailableChallengeStatusEnum, { text: TranslationKey, className: string }> = {
  [ChallengeService.AvailableChallengeStatusEnum.Available]: {
    text: 'challenges.startChallenge',
    className: 'bg-accent hover:bg-accent/80'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Unbooted]: {
    text: 'challenges.continue',
    className: 'bg-accent hover:bg-accent/80 ongoing-button-shadow'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Ongoing]: {
    text: 'challenges.continue',
    className: 'bg-accent hover:bg-accent/80 ongoing-button-shadow'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Cooldown]: {
    text: 'challenges.retakeInTIME',
    className: 'bg-accent/20 hover:bg-accent/30'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Finished]: {
    text: 'challenges.retake',
    className: 'bg-success hover:bg-success/80'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Aborted]: {
    text: 'challenges.resume',
    className: 'bg-warning hover:bg-warning/80'
  }
}

const getActionButton = (status: ChallengeService.AvailableChallengeStatusEnum): { text: TranslationKey, className: string } => {
  return actionButtonMap[status] || {
    text: 'challenges.startChallenge',
    className: 'bg-accent hover:bg-accent/80'
  }
}

const formatCooldownTime = (minutes: number): [TranslationKey, { x?: number }] => {
  const days = minutes / 24 / 60
  const hours = minutes / 60

  if (days > 2) {
    return ['times.xDays', { x: Math.round(days) }]
  }
  if (hours > 1) {
    return ['times.xHours', { x: Math.round(hours) }]
  }
  // If one hour or less, show minutes
  return minutes === 1
    ? ['times.oneMinute', {}]
    : ['times.xMinutes', { x: Math.round(minutes) }]
}

export const ChallengeCard: React.FC<ChallengeCardProps> = ({
  challenge,
  onInfoClick
}) => {
  const { t } = useTar()
  const { loggedInAction } = useAuth()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const IconComponent = challenge.icon || CommandLineIcon
  const actionButtonConfig = getActionButton(challenge.status)

  // Format cooldown time for display
  const cooldownTimeText = challenge.status === ChallengeService.AvailableChallengeStatusEnum.Cooldown && challenge.cooldownRemainingTime
    ? (() => t(...formatCooldownTime(challenge.cooldownRemainingTime)))()
    : null

  const handleActionClick = () => {
    loggedInAction(async () => {
      setIsLoading(true)
      try {
        if (challenge.status === ChallengeService.AvailableChallengeStatusEnum.Available) {
          const resp = await apis.challengeService.bootEnvironment({
            taskId: challenge.taskId
          })
          const slug = resp.data.slug
          if (slug) {
            sessionStorage.setItem('challenge_unbooted', slug)
            void queryClient.invalidateQueries({ queryKey: availableChallengesQueryKeys.all() })
            navigate({ to: `/challenge/${slug}` })
            return
          }
        }
        if (challenge.slug && challenge.status === ChallengeService.AvailableChallengeStatusEnum.Unbooted) {
          sessionStorage.setItem('challenge_unbooted', challenge.slug)
          navigate({ to: `/challenge/${challenge.slug}` })
          return
        }
        if (challenge.slug && challenge.status === ChallengeService.AvailableChallengeStatusEnum.Ongoing) {
          navigate({ to: `/challenge/${challenge.slug}` })
          return
        }
      } catch {
        toast.error(t('challenges.failedToStartChallenge'), {
          duration: 10000
        })
      } finally {
        setIsLoading(false)
      }
    }
    )
  }

  return (
    <div
      className="bg-card rounded-lg border border-border p-6 shadow-sm hover:shadow-lg transition-shadow duration-200 h-80 flex flex-col"
    >
      {/* Challenge Icon & Category */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-accent/10 rounded-lg">
            <IconComponent className="w-6 h-6 text-accent" />
          </div>
          <span className="text-xs font-medium text-muted uppercase tracking-wide">
            {challenge.language}
          </span>
        </div>

        {/* Difficulty Badge */}
        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getChallengeDifficultyColor(challenge.difficulty)}`}>
          {t(challengeDifficultyDisplayMap[challenge.difficulty])}
        </span>
      </div>

      {/* Challenge Title */}
      <h2 className="text-xl font-semibold text-foreground mb-3 h-12 leading-6 flex items-start">
        <span className="line-clamp-2">
          {challenge.name}
        </span>
      </h2>

      <DevPrint label="challengeId" value={challenge.challengeId} />
      <DevPrint label="taskId" value={challenge.taskId} />
      {/* Challenge Description */}
      <p className="text-muted-foreground text-sm mb-4 leading-relaxed flex-1">
        {challenge.teaser}
      </p>

      {/* Estimated Time */}
      {challenge.status === ChallengeService.AvailableChallengeStatusEnum.Cooldown && (
        <div className="flex items-center justify-start text-muted text-xs mb-6">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center">
                <span>{t('challenges.challengeInCooldown')}</span>
                <QuestionMarkCircleIcon className="w-4 h-4 ms-1 inline" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('challenges.cooldownDescription')}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          className={`flex-1 flex items-center justify-center gap-2 ${!isLoading ? actionButtonConfig.className : ''}`}
          variant={isLoading ? "outline" : "default"}
          onClick={handleActionClick}
          disabled={isLoading || challenge.status === ChallengeService.AvailableChallengeStatusEnum.Cooldown}
        >
          {isLoading ? (
            <StandardLoader size="md" inline />
          ) : cooldownTimeText ? (
            <span>{t(actionButtonConfig.text, { time: cooldownTimeText })}</span>
          ) : (
            <>
              <PlayIcon className="w-4 h-4" />
              <span>{t(actionButtonConfig.text)}</span>
            </>
          )}
        </Button>

        <button
          className="flex items-center justify-center px-3 py-2 border border-border rounded-md text-sm font-medium bg-card text-muted-foreground hover:bg-accent/10 hover:text-accent transition-colors duration-200"
          onClick={() => onInfoClick?.(challenge)}
        >
          <InformationCircleIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}

export type { Challenge, ChallengeCardProps }
